/**
 * Orden alfabético de las opciones de los desplegables.
 *
 * Se ordena SIEMPRE en el front y sobre la etiqueta ya resuelta, no sobre el `code` ni sobre
 * lo que devuelve el back: el texto visible sale de `i18n()` (JSON de idioma + overrides de
 * etiquetas por grupo) o de `catalog_translations`, así que el orden depende del idioma activo
 * y del grupo, y ninguna de las dos cosas la conoce el backend.
 *
 * El back solo debe ordenar cuando el orden decide QUÉ se ve (listados paginados o truncados
 * server-side), no cómo se ve.
 */

import { getLocalStorage } from './handleStorage';
import { i18n } from './i18n';

/**
 * Valores que representan una opción "vacía" (placeholder tipo «Todos», «Seleccione…»,
 * «Sin acceso»). Se quedan siempre arriba en vez de caer donde les toque por la inicial.
 */
const PINNED_VALUES = ['', 'NONE', 'ALL', 'UNASSIGNED'];

const isPinned = (option: any): boolean => {
  // En las listas de objetos manda `value`; en las listas planas de etiquetas, la propia etiqueta.
  const value = option !== null && typeof option === 'object' ? option.value : option;

  return (
    value === null || value === undefined || (typeof value === 'string' && PINNED_VALUES.includes(value.toUpperCase()))
  );
};

/**
 * Texto por el que se ordena una etiqueta. Algunas listas de opciones traen como `label` el
 * elemento JSX que devuelve `i18Intl` (`<FormattedMessage id="…"/>`); en ese caso se ordena por
 * la traducción de su `id`, y lo que se pinta sigue siendo el elemento original.
 */
export const resolveLabelText = (label: any): any =>
  label !== null && typeof label === 'object' && label?.props?.id !== undefined ? i18n(label.props.id) : label;

/** Comparador del idioma activo: ignora tildes y mayúsculas, y ordena «Nivel 2» antes que «Nivel 10». */
export const getLabelCollator = (): Intl.Collator =>
  new Intl.Collator(getLocalStorage('locale') || 'es', { sensitivity: 'base', numeric: true });

/**
 * Devuelve una copia de `options` ordenada alfabéticamente por su etiqueta.
 *
 * - No muta el array recibido.
 * - Las opciones placeholder (ver `PINNED_VALUES`) quedan al principio, en su orden original.
 * - Si alguna etiqueta no es texto (p.ej. el elemento JSX que devuelve `i18Intl`), devuelve la
 *   lista intacta: no se puede comparar de forma fiable y es preferible no tocar el orden.
 */
export function sortOptionsByLabel<T>(options: T[], getLabel: (option: T) => any = (option: any) => option?.label): T[] {
  if (!Array.isArray(options) || options.length < 2) {
    return options;
  }

  const labels = options.map((option) => resolveLabelText(getLabel(option)));

  if (labels.some((label) => typeof label !== 'string' && typeof label !== 'number')) {
    return options;
  }

  const collator = getLabelCollator();

  return options
    .map((option, index) => ({ option, index, label: String(labels[index]) }))
    .sort((a, b) => {
      const aPinned = isPinned(a.option);
      const bPinned = isPinned(b.option);

      if (aPinned !== bPinned) {
        return aPinned ? -1 : 1;
      }
      if (aPinned && bPinned) {
        return a.index - b.index;
      }
      return collator.compare(a.label, b.label);
    })
    .map((item) => item.option);
}

/** Variante para listas de etiquetas planas (el `MultiSelect` legacy trabaja con strings sueltos). */
export const sortLabels = (labels: string[]): string[] => sortOptionsByLabel(labels, (label) => label);
